import React, { useState, useEffect } from 'react';

import { 
  Play, RotateCcw, Plus, Type, List, Timer, Search, Eye, 
  SlidersHorizontal, Info, Download, X, Menu, ArrowRight, 
  Zap, Film, Music, Check, ChevronRight
} from 'lucide-react';


// Films whose closing credits were made with BLACKBOXcr (confirmed by the
// founder). Add poster paths (e.g. "/posters/jackrabbit.jpg" in public/) when
// the artwork arrives; until then the title is shown in the poster slot.
const USED_ON = [
  { slug: "jackrabbit", title: "Jackrabbit", poster: null },
  { slug: "cultus", title: "Cultus", poster: null },
  { slug: "mercy-mercy-me", title: "Mercy Mercy Me", poster: null },
  { slug: "wildcards", title: "Wildcards", poster: null },
];

// --- SUB-COMPONENT: The Command Bar (Top Positioned) ---
const CommandBarUI = () => (
  <div className="inline-flex items-center gap-1 bg-zinc-950/90 backdrop-blur-md border border-zinc-800 rounded-full px-5 py-3 shadow-2xl">
    {/* Playback Controls */}
    <div className="flex items-center gap-5 px-2 border-r border-zinc-800 pr-5">
      <Play size={20} className="text-zinc-200 hover:text-white cursor-pointer transition-colors" fill="currentColor" />
      <RotateCcw size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
    </div>
    
    {/* Editor Tools */}
    <div className="flex items-center gap-5 px-5 border-r border-zinc-800">
      <Plus size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <Type size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <List size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <Timer size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
    </div>

    {/* View/Export Controls */}
    <div className="flex items-center gap-5 px-2 pl-5">
      <Search size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <Eye size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <SlidersHorizontal size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <Info size={20} className="text-zinc-400 hover:text-white cursor-pointer transition-colors" />
      <Download size={20} className="text-green-500 hover:text-green-400 cursor-pointer transition-colors" />
    </div>
  </div>
);

// --- SUB-COMPONENT: Live Scrolling Credits Animation ---
const LiveCreditScroll = () => {
  // Your actual cast data from the screenshot
  const castList = [
    { role: "Greg Sims", name: "LEON" },
    { role: "Juror#2 / Karla", name: "ELISE NEAL" },
    { role: "James Jones", name: "ARIES SPEARS" },
    { role: "Bailey", name: "STAKIAH LYNN WASHINGTON" },
    { role: "Theodore Sterling", name: "CREW MORROW" },
    { role: "Amber Sterling", name: "COURTNEY GRACE" },
    { role: "Leah Hamilton", name: "SAMANTHA WALKES" },
    { role: "Judge Brown", name: "ROBERT CATRINI" },
    { role: "Faith Childs", name: "YADI NIEVES" },
    { role: "Juror#1 / Reed", name: "BRIAN STAPF" }
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none bg-black">
      <style>{`
        @keyframes creditScroll {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }
        .animate-credits {
          animation: creditScroll 45s linear infinite;
          will-change: transform;
        }
      `}</style>

      {/* The Scrolling Content Container */}
      <div className="animate-credits w-full py-20 opacity-90">
        {/* We duplicate the content to create a seamless loop */}
        {[1, 2].map((i) => (
          <div key={i} className="pb-32">
            
            {/* 2. Two Column: CAST Header */}
            <div className="text-center mb-12">
               <p className="text-white text-lg font-bold tracking-[0.2em] uppercase">CAST</p>
            </div>

            {/* 3. Two Column: CAST LIST */}
            <div className="w-full max-w-3xl mx-auto px-10 space-y-6 mb-32">
              {castList.map((actor, idx) => (
                <div key={idx} className="grid grid-cols-2 gap-12 items-baseline">
                  {/* Left Col: Character Name */}
                  <div className="text-right">
                    <span className="text-zinc-400 text-lg font-medium tracking-wide">{actor.role}</span>
                  </div>
                  {/* Right Col: Actor Name */}
                  <div className="text-left">
                    <span className="text-white text-xl font-bold tracking-widest uppercase">{actor.name}</span>
                  </div>
                </div>
              ))}
            </div>
             
             {/* Spacer for loop */}
             <div className="h-32"></div>
          </div>
        ))}
      </div>
      
      {/* Gradient Masks */}
      <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-black via-black/80 to-transparent z-10"></div>
      <div className="absolute bottom-0 left-0 w-full h-40 bg-gradient-to-t from-black via-black/80 to-transparent z-10"></div>
    </div>
  );
};

const LandingPage = ({ onNavigate, setPurchaseIntent }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
  }, [isMenuOpen]);

  const handleGetStarted = () => {
    setPurchaseIntent('demo');
    onNavigate('signup');
  };

  const handleNav = (page) => {
    setIsMenuOpen(false);
    onNavigate(page);
  };

  const handleScrollTo = (id) => {
    setIsMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-zinc-950 text-zinc-100 font-sans selection:bg-purple-500/30 selection:text-purple-200">
      
      {/* 1. MINIMALIST NAVIGATION */}
      <nav className="fixed top-0 w-full z-40 border-b border-white/5 bg-zinc-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex justify-between items-center">
          <div 
            className="cursor-pointer flex items-center gap-2 group" 
            onClick={() => handleNav('landing')}
          >
            <div className="text-xl font-medium tracking-tight text-white group-hover:text-purple-400 transition-colors">
              BLACKBOX<span className="text-zinc-500">cr</span>
            </div>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8 text-sm font-medium">
            <button onClick={() => handleNav('features')} className="text-zinc-400 hover:text-white transition-colors">Features</button>
            <button onClick={() => handleScrollTo('how-it-works')} className="text-zinc-400 hover:text-white transition-colors">How it Works</button>
            <button onClick={() => handleScrollTo('pricing')} className="text-zinc-400 hover:text-white transition-colors">Pricing</button>
            <div className="h-4 w-px bg-zinc-800"></div>
            <button onClick={() => handleNav('login')} className="text-white hover:text-purple-300 transition-colors">Sign In</button>
            <button 
              onClick={handleGetStarted}
              className="bg-white text-black hover:bg-zinc-200 px-4 py-2 rounded-full transition-all"
            >
              Start Free
            </button>
          </div>
          
          {/* Mobile Toggle */}
          <div className="md:hidden">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white p-2">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-zinc-950 z-30 pt-24 px-6 space-y-6">
          <button onClick={() => handleNav('features')} className="block text-2xl font-light text-zinc-300">Features</button>
          <button onClick={() => handleScrollTo('how-it-works')} className="block text-2xl font-light text-zinc-300">How it Works</button>
          <button onClick={() => handleScrollTo('pricing')} className="block text-2xl font-light text-zinc-300">Pricing</button>
          <div className="h-px bg-zinc-800 w-full my-6"></div>
          <button onClick={() => handleNav('login')} className="block text-2xl font-medium text-white">Sign In</button>
          <button onClick={handleGetStarted} className="w-full bg-white text-black py-4 rounded-lg font-medium text-xl mt-4">Start Free Trial</button>
        </div>
      )}

      {/* 2. HERO SECTION: Live Render */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden">
        {/* Subtle Background Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-purple-900/10 blur-[120px] rounded-full pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-400 mb-8 hover:border-zinc-700 transition-colors cursor-default">
            <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></span>
            Version 2.0 Now Live
          </div>
          
          <h1 className="text-sm md:text-base font-medium tracking-wide text-purple-400 mb-6">
            BLACKBOXcr: closing credits software for independent films
          </h1>
          <p className="text-5xl md:text-7xl font-semibold tracking-tight text-white mb-6 leading-[1.1]">
            The final cut,<br />
            <span className="text-zinc-500">perfected.</span>
          </p>
          
          <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed font-light">
            Create studio-quality scrolling credits in minutes.
            <br />
            <span className="text-white font-medium">No After Effects. No rendering delays. Just perfect typography.</span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
            <button 
              onClick={handleGetStarted}
              className="w-full sm:w-auto px-8 py-4 bg-white text-black font-medium rounded-lg hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
            >
              Start Creating
              <ArrowRight size={18} />
            </button>
            <button 
              onClick={() => handleScrollTo('how-it-works')}
              className="w-full sm:w-auto px-8 py-4 bg-zinc-900 border border-zinc-800 text-zinc-300 font-medium rounded-lg hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2"
            >
              How it works
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* HERO INTERFACE VISUALIZATION */}
        <div className="max-w-5xl mx-auto relative">
          {/* The Interface Container */}
          <div className="aspect-video bg-black rounded-xl border border-zinc-800 shadow-2xl overflow-hidden relative group">
             
             {/* 1. The LIVE Scroll Preview (Background) */}
             <LiveCreditScroll />

             {/* 2. The Command Bar (Floating Top Overlay) */}
             <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-20 hover:scale-105 transition-transform duration-300">
                <CommandBarUI />
             </div>

          </div>
          
          {/* Reflection Effect */}
          <div className="absolute -bottom-20 left-4 right-4 h-20 bg-gradient-to-b from-purple-500/5 to-transparent blur-xl"></div>
        </div>
      </section>

      {/* 3. SOCIAL PROOF */}
      <section className="border-y border-white/5 bg-black py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-sm text-zinc-500 mb-8 font-medium tracking-widest uppercase">Closing credits made with BLACKBOXcr</h2>
          <ul className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {USED_ON.map((film) => (
              <li key={film.slug}>
                <a
                  href={`https://www.blackbox-is.com/resources/case-studies/${film.slug}`}
                  className="group block"
                >
                  <div className="aspect-[2/3] rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden flex items-center justify-center group-hover:border-zinc-600 transition-colors">
                    {film.poster ? (
                      <img src={film.poster} alt={`${film.title} poster`} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span className="px-4 text-xl font-semibold text-zinc-300">{film.title}</span>
                    )}
                  </div>
                  <span className="block mt-3 text-sm text-zinc-300 group-hover:text-white">{film.title}</span>
                </a>
              </li>
            ))}
          </ul>
          <a
            href="https://www.blackbox-is.com/resources/tools/scroll-calculator"
            className="inline-block mt-10 text-sm text-zinc-400 hover:text-white underline underline-offset-4"
          >
            Free tool: Cinematic Scroll Calculator for judder-free credit rolls
          </a>
        </div>
      </section>

      {/* 4. FEATURES GRID */}
      <section id="how-it-works" className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20 max-w-2xl">
            <h2 className="text-3xl md:text-5xl font-semibold mb-6">Built for the deadline.</h2>
            <p className="text-xl text-zinc-400">
              Credits are usually the last step, done in a panic. We built a tool that respects your time and your film's quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Feature 1: Command Bar Focus */}
            <div className="md:col-span-2 bg-zinc-900/30 border border-white/5 rounded-3xl p-8 md:p-12 relative overflow-hidden group hover:border-zinc-700 transition-colors flex flex-col justify-between">
              <div className="relative z-10 mb-20">
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6 border border-purple-500/20">
                  <Zap className="text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold mb-4">The Command Bar</h3>
                <p className="text-zinc-400 max-w-md">
                  Everything you need, right where you need it. 
                  Global styling, timing adjustments, and formatting tools in one floating palette.
                </p>
              </div>
              
              {/* Visual for Command Bar */}
              <div className="absolute right-8 bottom-8 z-10 transform group-hover:-translate-y-2 transition-transform duration-500">
                 <div className="scale-75 origin-bottom-right">
                    <CommandBarUI />
                 </div>
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-zinc-900/80"></div>
            </div>

            {/* Feature 2: Preview */}
            <div className="row-span-2 bg-zinc-900/30 border border-white/5 rounded-3xl p-8 md:p-12 relative overflow-hidden group hover:border-zinc-700 transition-colors">
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-6 border border-blue-500/20">
                <Film className="text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4">WYSIWYG Preview</h3>
              <p className="text-zinc-400 mb-8">
                What you see is exactly what you export. Playback your scroll in real-time.
              </p>
            </div>

            {/* Feature 3: Export */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 relative overflow-hidden group hover:border-zinc-700 transition-colors">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center mb-6 border border-green-500/20">
                <Download className="text-green-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">H.264, H.265, ProRes 422, ProRes 4444</h3>
              <p className="text-zinc-400 text-sm">
                Industry standard export formats including Alpha Channel support for overlaying on footage up to 4K.
              </p>
            </div>

            {/* Feature 4: Sync */}
            <div className="bg-zinc-900/30 border border-white/5 rounded-3xl p-8 relative overflow-hidden group hover:border-zinc-700 transition-colors">
              <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center mb-6 border border-orange-500/20">
                <Music className="text-orange-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Music Sync</h3>
              <p className="text-zinc-400 text-sm">
                Upload your score. We automatically calculate the perfect scroll speed to match the duration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PRICING CARDS */}
      <section id="pricing" className="py-24 px-6 bg-zinc-900/20 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold mb-6">Simple, per-project pricing.</h2>
            <p className="text-zinc-400">No subscriptions. Pay only when you are ready to export.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
             {/* Tier 1 */}
            <div className="p-8 rounded-2xl border border-white/10 bg-zinc-950 flex flex-col">
              <h3 className="text-lg font-medium text-zinc-300">Free Trial</h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-white">$0</span>
              </div>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> 7-day full access
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> Unlimited previews
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> Demo resolution exports
                </li>
              </ul>
              <button onClick={handleGetStarted} className="w-full py-3 rounded-lg border border-zinc-700 text-white hover:bg-zinc-900 transition-colors">
                Start Trial
              </button>
            </div>

            {/* Tier 2 */}
            <div className="p-8 rounded-2xl border border-white/10 bg-zinc-950 flex flex-col">
              <h3 className="text-lg font-medium text-zinc-300">Indie Project</h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-white">$199</span>
                <span className="text-zinc-500 ml-2">/ film</span>
              </div>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> 1080p Export
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> H.264 & .mp4 formats
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-400">
                  <Check size={16} className="text-white mt-0.5" /> 90 days access
                </li>
              </ul>
              <button 
                onClick={() => { setPurchaseIntent('indie'); onNavigate('signup'); }}
                className="w-full py-3 rounded-lg bg-zinc-100 text-black hover:bg-white transition-colors font-medium"
              >
                Choose Indie
              </button>
            </div>

            {/* Tier 3 */}
            <div className="p-8 rounded-2xl border border-purple-500/30 bg-purple-900/10 flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                MOST POPULAR
              </div>
              <h3 className="text-lg font-medium text-white">Feature Film</h3>
              <div className="mt-4 mb-6">
                <span className="text-4xl font-bold text-white">$399</span>
                <span className="text-zinc-500 ml-2">/ film</span>
              </div>
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-purple-400 mt-0.5" /> 4K Resolution
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-purple-400 mt-0.5" /> ProRes 4444 (Alpha)
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-purple-400 mt-0.5" /> NLE Data Export
                </li>
                <li className="flex items-start gap-3 text-sm text-zinc-300">
                  <Check size={16} className="text-purple-400 mt-0.5" /> Priority Support
                </li>
              </ul>
              <button 
                onClick={() => { setPurchaseIntent('pro'); onNavigate('signup'); }}
                className="w-full py-3 rounded-lg bg-purple-600 text-white hover:bg-purple-500 transition-colors font-medium shadow-lg shadow-purple-900/20"
              >
                Choose Pro
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FOOTER */}
      <footer className="py-12 border-t border-white/5 text-center bg-zinc-950">
        <div className="text-2xl font-medium tracking-tight text-zinc-800 mb-6">
              BLACKBOX<span className="text-zinc-800">cr</span>
        </div>
        <div className="flex justify-center gap-8 text-sm text-zinc-500 mb-8">
          <button onClick={() => onNavigate('features')} className="hover:text-zinc-300 transition-colors">Features</button>
          <button onClick={() => onNavigate('terms')} className="hover:text-zinc-300 transition-colors">Terms</button>
          <button onClick={() => onNavigate('privacy')} className="hover:text-zinc-300 transition-colors">Privacy</button>
          <a href="mailto:support@blackbox-is.com" className="hover:text-zinc-300 transition-colors">Contact</a>
        </div>
        <p className="text-zinc-600 text-xs">© 2025 BLACKBOXis. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;